/*
 * EventChannel: a positional-argument event channel, offered beside the
 * payload-object `on/off` every component has.
 *
 *   toolbar.events.on('click', (id, event) => { ... });
 *   toolbar.events.on('click', handler, owner);     // owner: any object, used by detach
 *   toolbar.events.detach('click', owner);          // drop the handlers registered with owner
 *   toolbar.events.detach('click');                 // drop every handler for the event
 *   toolbar.events.fire('click', ['save']);         // runs the component's own behaviour too
 *
 * Event names are case-insensitive. Handlers run in registration order with
 * `this` bound to their owner (or the component). `fire` returns false when
 * any handler returned false, which is how `before…` events are vetoed.
 *
 * `events.events` is the live store: { [name]: [{ callback, context }] }.
 * Reading it, or replacing a list with a filtered copy, is supported; `fire`
 * works on a snapshot, so changing the lists from inside a handler is safe.
 */

export class EventChannel {
	/**
	 * @param {Object} owner - The component; the default `this` for handlers.
	 * @param {Function} [before] - (name, args) => void|false, the component's own
	 *   behaviour, run before any handler on every fire (including a host's);
	 *   returning false makes `fire` return false.
	 * @param {Function} [after] - (name, args) => void, run after the handlers.
	 */
	constructor(owner, before, after) {
		this.context = owner;
		this.events = {};
		this._before = before || null;
		this._after = after || null;
	}

	/** Adds a handler. @returns {Function} the handler, for symmetry with detach. */
	on(name, callback, context) {
		if (typeof callback !== 'function') return callback;
		const key = String(name).toLowerCase();
		(this.events[key] = this.events[key] || []).push({ callback, context: context === undefined ? this.context : context });
		return callback;
	}

	/**
	 * Removes handlers. With an owner (or the handler function itself) only the
	 * matching ones go; with neither, every handler for the event goes.
	 */
	detach(name, contextOrCallback) {
		const key = String(name).toLowerCase();
		const list = this.events[key];
		if (!list) return;
		if (contextOrCallback === undefined) { this.events[key] = []; return; }
		this.events[key] = list.filter((h) => h.context !== contextOrCallback && h.callback !== contextOrCallback);
	}

	/** Alias of detach for one handler. */
	off(name, callback) { this.detach(name, callback); }

	/** The handlers registered for an event, as { callback, context } entries. */
	list(name) { return (this.events[String(name).toLowerCase()] || []).slice(); }

	/**
	 * Runs the component's behaviour, then every handler with `args` spread.
	 * @param {string} name @param {Array} [args] @returns {boolean} false if a handler returned false.
	 */
	fire(name, args = []) {
		const key = String(name).toLowerCase();
		const list = Array.isArray(args) ? args : [args];
		let ok = true;
		if (this._before && this._before(key, list) === false) ok = false;
		for (const h of (this.events[key] || []).slice()) {
			try {
				if (h.callback.apply(h.context, list) === false) ok = false;
			} catch (e) {
				console.error(`dfc: error in "${name}" handler`, e);
			}
		}
		if (this._after) this._after(key, list);
		return ok;
	}

	/** Drops every handler for every event. */
	clear() { this.events = {}; }
}
